import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setScrolled(window.scrollY > 50);
    });
  }

  const services = [
    {
      title: 'Chimney Repair & Exhaust Fan Service',
      description: 'We repair chimney exhaust fans, blocked flues, airflow issues, and damaged venting systems to ensure proper draft and safety.',
      image: 'https://public.readdy.ai/ai/img_res/71b75cd065f5547c6b37cfdad740732d.jpg'
    },
    {
      title: 'Rooftop Ventilation Unit Repair',
      description: 'Expert servicing and repair of roof-mounted ventilation and exhaust units for residential and commercial properties.',
      image: 'https://readdy.ai/api/search-image?query=rooftop%20ventilation%20unit%20being%20serviced%20by%20professional%20technician%20commercial%20building%20rooftop%20setting%20industrial%20exhaust%20system%20clear%20background%20professional%20service%20photography%20natural%20daylight&width=800&height=600&seq=rooftop002&orientation=landscape'
    },
    {
      title: 'Fireplace Repair & Restoration',
      description: 'From airflow problems to structural repairs, we restore fireplaces to safe, efficient operation.',
      image: 'https://readdy.ai/api/search-image?query=elegant%20brick%20fireplace%20being%20restored%20by%20expert%20technician%20cozy%20interior%20setting%20professional%20tools%20visible%20warm%20ambient%20lighting%20clean%20simple%20background%20high%20quality%20service%20photography&width=800&height=600&seq=fireplace003&orientation=landscape'
    },
    {
      title: 'Ventilation System Repair',
      description: 'Complete diagnostics and repairs for ventilation units to improve indoor air quality and system performance.',
      image: 'https://readdy.ai/api/search-image?query=modern%20ventilation%20system%20ductwork%20being%20inspected%20and%20repaired%20professional%20technician%20with%20diagnostic%20tools%20clean%20industrial%20setting%20bright%20lighting%20simple%20background%20technical%20service%20photography&width=800&height=600&seq=ventilation004&orientation=landscape'
    }
  ];

  const features = [
    {
      title: 'Licensed & Experienced',
      description: 'Fully certified technicians with extensive training and industry expertise',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Fast Response Times',
      description: 'Same-day and priority service available for urgent repairs',
      icon: 'ri-time-line'
    },
    {
      title: 'Transparent Pricing',
      description: 'Clear written proposals with labor and parts included upfront',
      icon: 'ri-file-list-3-line'
    },
    {
      title: 'Safety Focused',
      description: 'All repairs meet or exceed local building codes and safety standards',
      icon: 'ri-shield-star-line'
    },
    {
      title: 'Residential & Commercial',
      description: 'Expert service for both homeowners and business properties',
      icon: 'ri-building-line'
    },
    {
      title: 'Quality Workmanship',
      description: 'Repairs done right the first time with lasting results',
      icon: 'ri-tools-line'
    }
  ];

  const testimonials = [
    {
      name: 'Michael Rodriguez',
      role: 'Homeowner',
      text: 'Fast, professional service. They fixed our chimney exhaust fan the same day. Highly recommend!',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20middle%20aged%20man%20smiling%20portrait%20clean%20simple%20light%20background%20friendly%20approachable%20headshot%20natural%20lighting&width=200&height=200&seq=avatar001&orientation=squarish'
    },
    {
      name: 'Sarah Chen',
      role: 'Property Manager',
      text: 'Excellent work on our commercial ventilation system. Clear pricing and expert technicians.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20woman%20smiling%20portrait%20business%20attire%20clean%20simple%20light%20background%20confident%20friendly%20headshot%20natural%20lighting&width=200&height=200&seq=avatar002&orientation=squarish'
    },
    {
      name: 'James Thompson',
      role: 'Restaurant Owner',
      text: 'They restored our kitchen exhaust system quickly. Very knowledgeable and reliable service.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20with%20beard%20smiling%20portrait%20casual%20business%20attire%20clean%20simple%20light%20background%20warm%20friendly%20headshot%20natural%20lighting&width=200&height=200&seq=avatar003&orientation=squarish'
    },
    {
      name: 'Linda Martinez',
      role: 'Homeowner',
      text: 'Our fireplace works perfectly now. The technician was thorough and explained everything clearly.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20smiling%20portrait%20elegant%20appearance%20clean%20simple%20light%20background%20friendly%20warm%20headshot%20natural%20lighting&width=200&height=200&seq=avatar004&orientation=squarish'
    },
    {
      name: 'David Park',
      role: 'Building Superintendent',
      text: 'Honest evaluation and fair pricing. They fixed our rooftop ventilation unit efficiently.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20man%20smiling%20portrait%20business%20casual%20attire%20clean%20simple%20light%20background%20confident%20friendly%20headshot%20natural%20lighting&width=200&height=200&seq=avatar005&orientation=squarish'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'On-Site Inspection',
      description: 'One-hour evaluation to assess the issue accurately and provide honest recommendations.'
    },
    {
      number: '02',
      title: 'Upfront Proposal',
      description: 'Clear, detailed pricing with total repair cost including labor and parts.'
    },
    {
      number: '03',
      title: 'Expert Repair',
      description: 'Efficient, high-quality repairs that last, completed by certified technicians.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className={`w-10 h-10 flex items-center justify-center ${scrolled ? 'bg-teal-600' : 'bg-white'} rounded-lg`}>
                  <i className={`ri-fire-line text-xl ${scrolled ? 'text-white' : 'text-teal-600'}`}></i>
                </div>
                <div className="flex flex-col">
                  <span className={`text-xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>Readdy Services</span>
                  <span className={`text-xs ${scrolled ? 'text-gray-600' : 'text-white/80'}`}>Venting & Exhaust Install Repair Company</span>
                </div>
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>Services</a>
              <a href="#process" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>Process</a>
              <a href="#why-us" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>Why Us</a>
              <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>Contact</a>
              <a href="tel:6463081336" className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                Call 646-308-1336
              </a>
            </div>

            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-3">
              <a href="#services" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">Services</a>
              <a href="#process" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">Process</a>
              <a href="#why-us" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">Why Us</a>
              <a href="#contact" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">Contact</a>
              <a href="tel:6463081336" className="block bg-teal-600 text-white px-6 py-2.5 rounded-full text-center font-medium hover:bg-teal-700 transition-colors cursor-pointer">
                Call 646-308-1336
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=professional%20technician%20working%20on%20rooftop%20chimney%20ventilation%20system%20during%20golden%20hour%20sunset%20wide%20angle%20view%20urban%20skyline%20background%20warm%20natural%20lighting%20safety%20equipment%20visible%20high%20quality%20professional%20service%20photography%20clean%20simple%20composition&width=1920&height=1080&seq=hero001&orientation=landscape"
            alt="Professional chimney repair service"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Readdy Services - Venting & Exhaust Install Repair Company
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl font-light">
              Fast. Professional. Trusted by Homeowners & Businesses.
            </p>
            <p className="text-lg text-white/80 mb-10 max-w-2xl leading-relaxed">
              Get expert repair for chimney exhaust fans, rooftop ventilation units, fireplaces, and exhaust systems. Our certified technicians deliver safe, code-compliant, and long-lasting solutions—done right the first time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:6463081336"
                className="inline-flex items-center justify-center bg-white text-gray-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer group"
              >
                <span>Call : 646-308-1336</span>
                <div className="ml-3 w-10 h-10 flex items-center justify-center bg-teal-600 rounded-full group-hover:bg-teal-700 transition-colors">
                  <i className="ri-phone-line text-white text-lg"></i>
                </div>
              </a>
              <a 
                href="#services"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer"
              >
                View Services
                <i className="ri-arrow-down-line ml-2 text-xl"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-s-line text-white text-4xl"></i>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Premium Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive chimney, ventilation, and exhaust repair solutions for residential and commercial properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-96"
              >
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-white/90 text-base leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-settings-3-line mr-2"></i>
              Simple & Transparent
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Service Process
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Three simple steps to get your chimney or ventilation system repaired professionally
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-shadow h-full">
                  <div className="text-6xl font-bold text-teal-600/20 mb-4">{step.number}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-3xl text-teal-600"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a 
              href="tel:6463081336"
              className="inline-flex items-center bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-3 text-xl"></i>
              Call 646-308-1336 to Get Started
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Customers Choose Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted by homeowners and businesses across New York for reliable chimney and ventilation repair
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:bg-teal-50 transition-colors group cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-teal-600 rounded-xl mb-6 group-hover:bg-teal-700 transition-colors">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-star-fill mr-2"></i>
              Customer Reviews
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Trusted by 500+ Property Owners
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img 
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover object-top mr-4"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Fix Your Chimney or Vent Today
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Protect your property, restore airflow, and ensure safety with professional chimney, ventilation, and exhaust fan repair. Call now to schedule your inspection and get expert service you can trust.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:6463081336"
              className="inline-flex items-center justify-center bg-white text-teal-600 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-3 text-xl"></i>
              Call 646-308-1336
            </a>
            <a 
              href="mailto:nkrservicecalendar@gmail.com"
              className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-3 text-xl"></i>
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Contact Us
            </h2>
            <p className="text-xl text-gray-600">
              Schedule your inspection today and get expert service you can rely on
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Service Address</h3>
              <p className="text-gray-600 leading-relaxed">
                39 West 29th Street, 6th Floor<br />
                New York, NY 10001
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Phone</h3>
              <a href="tel:6463081336" className="text-teal-600 hover:text-teal-700 font-semibold text-lg cursor-pointer">
                646-308-1336
              </a>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
              <a href="mailto:nkrservicecalendar@gmail.com" className="text-teal-600 hover:text-teal-700 font-semibold break-all cursor-pointer">
                nkrservicecalendar@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg">
                  <i className="ri-fire-line text-xl text-white"></i>
                </div>
                <span className="text-xl font-bold">Readdy Services</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Professional chimney, ventilation, and exhaust fan repair services for residential and commercial properties in New York.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase text-gray-400 mb-4">Services</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Chimney Repair</a></li>
                <li><a href="#services" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Ventilation Repair</a></li>
                <li><a href="#services" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Fireplace Repair</a></li>
                <li><a href="#services" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Exhaust Fan Repair</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase text-gray-400 mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Services</a></li>
                <li><a href="#process" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Process</a></li>
                <li><a href="#why-us" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Why Us</a></li>
                <li><a href="#contact" className="text-white hover:text-teal-400 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase text-gray-400 mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-phone-line text-teal-400 mt-1 mr-3"></i>
                  <a href="tel:6463081336" className="text-white hover:text-teal-400 transition-colors cursor-pointer">646-308-1336</a>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line text-teal-400 mt-1 mr-3"></i>
                  <a href="mailto:nkrservicecalendar@gmail.com" className="text-white hover:text-teal-400 transition-colors break-all cursor-pointer">nkrservicecalendar@gmail.com</a>
                </li>
                <li className="flex items-start">
                  <i className="ri-map-pin-line text-teal-400 mt-1 mr-3"></i>
                  <span className="text-white">39 West 29th Street, 6th Floor, New York, NY 10001</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2025 Readdy Services - Venting & Exhaust Install Repair Company. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}